import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import { genStyles } from '../converter-layout/generator-layout';
import styles from './csv-converter.component.module.scss';

interface CsvState {
    input: string;
    output: string;
    delimiter: string;
    hasHeaders: boolean;
    error: string;
    copied: boolean;
    preview: string[][];
    csvToJson: boolean;
}

const DELIMITERS: Record<string, string> = { ',': 'Comma', '\t': 'Tab', ';': 'Semicolon', '|': 'Pipe' };

function parseCsv(text: string, delimiter: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '"' && text[i + 1] === '"') { field += '"'; i++; }
            else if (ch === '"') inQuotes = false;
            else field += ch;
        } else if (ch === '"') {
            inQuotes = true;
        } else if (ch === delimiter) {
            row.push(field); field = '';
        } else if (ch === '\n' || (ch === '\r' && text[i + 1] === '\n')) {
            row.push(field); field = '';
            if (row.some(c => c !== '')) rows.push(row);
            row = [];
            if (ch === '\r') i++;
        } else {
            field += ch;
        }
    }
    row.push(field);
    if (row.some(c => c !== '')) rows.push(row);
    return rows;
}

function csvToJson(text: string, delimiter: string, hasHeaders: boolean): { json: string; preview: string[][] } {
    const rows = parseCsv(text, delimiter);
    if (!rows.length) return { json: '[]', preview: [] };
    if (hasHeaders) {
        const headers = rows[0];
        const data = rows.slice(1).map(r => {
            const obj: Record<string, string> = {};
            headers.forEach((h, i) => { obj[h.trim() || `col${i}`] = r[i] ?? ''; });
            return obj;
        });
        return { json: JSON.stringify(data, null, 2), preview: rows.slice(0, 6) };
    }
    return { json: JSON.stringify(rows, null, 2), preview: rows.slice(0, 5) };
}

function jsonToCsv(text: string, delimiter: string): { csv: string; preview: string[][] } {
    const data = JSON.parse(text);
    if (!Array.isArray(data) || !data.length) return { csv: '', preview: [] };
    if (typeof data[0] === 'object' && !Array.isArray(data[0])) {
        const keys = Object.keys(data[0]);
        const escape = (v: unknown) => {
            const s = String(v ?? '');
            return s.includes(delimiter) || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const lines = [keys.map(escape).join(delimiter), ...data.map((row: Record<string, unknown>) => keys.map(k => escape(row[k])).join(delimiter))];
        const preview = [keys, ...data.slice(0, 5).map((row: Record<string, unknown>) => keys.map(k => String(row[k] ?? '')))];
        return { csv: lines.join('\n'), preview };
    }
    const lines = data.map((row: unknown[]) => row.map(v => String(v ?? '')).join(delimiter));
    return { csv: lines.join('\n'), preview: data.slice(0, 5).map((r: unknown[]) => r.map(v => String(v ?? ''))) };
}

export default class UcsvUconverterComponent extends Component<{ feature?: string }, CsvState> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    private fileInput: HTMLInputElement | null = null;

    state: CsvState = { input: '', output: '', delimiter: ',', hasHeaders: true, error: '', copied: false, preview: [], csvToJson: this.props.feature !== 'convert-json-to-csv' };

    get isCsvToJson(): boolean {
        return this.state.csvToJson;
    }

    handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => { this.convert(reader.result as string); };
        reader.readAsText(file);
        e.target.value = '';
    };

    convert = (input?: string, delimiter?: string, hasHeaders?: boolean) => {
        const inp = input ?? this.state.input;
        const delim = delimiter ?? this.state.delimiter;
        const headers = hasHeaders ?? this.state.hasHeaders;
        this.setState({ input: inp, delimiter: delim, hasHeaders: headers, error: '', copied: false });
        if (!inp.trim()) { this.setState({ output: '', preview: [] }); return; }
        try {
            if (this.isCsvToJson) {
                const { json, preview } = csvToJson(inp, delim, headers);
                this.setState({ output: json, preview });
            } else {
                const { csv, preview } = jsonToCsv(inp, delim);
                this.setState({ output: csv, preview });
            }
        } catch {
            this.setState({ error: this.isCsvToJson ? 'Invalid CSV input' : 'Invalid JSON input', output: '', preview: [] });
        }
    };

    copy = () => {
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    swap = () => {
        this.setState(s => ({
            csvToJson: !s.csvToJson, input: s.output, output: '', error: '', preview: [],
        }), () => this.convert(this.state.input));
    };

    render() {
        const { input, output, delimiter, hasHeaders, error, copied, preview } = this.state;
        const inputLabel = this.isCsvToJson ? 'CSV' : 'JSON';
        const outputLabel = this.isCsvToJson ? 'JSON' : 'CSV';

        return (
            <div className="row">
                <div className="col-12">
                    <div className={genStyles.section}>
                        <div className={styles.options}>
                            <label className={styles.checkbox}>
                                Delimiter:
                                <select className={styles.select} value={delimiter}
                                    onChange={e => this.convert(undefined, e.target.value, undefined)}>
                                    {Object.entries(DELIMITERS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                </select>
                            </label>
                            {this.isCsvToJson && (
                                <label className={styles.checkbox}>
                                    <input type="checkbox" checked={hasHeaders}
                                        onChange={e => this.convert(undefined, undefined, e.target.checked)} />
                                    First row as headers
                                </label>
                            )}
                            <button className={styles.swapButton} onClick={this.swap} title="Swap direction">⇄</button>
                        </div>
                        <div className={styles.grid}>
                            <div>
                                <div className={styles.header}>
                                    <label className={sharedStyles.label}>{inputLabel} Input</label>
                                    <input type="file" ref={el => { this.fileInput = el; }}
                                        accept={this.isCsvToJson ? '.csv,.tsv,.txt' : '.json'}
                                        onChange={this.handleFileUpload} style={{ display: 'none' }} />
                                    <button className={sharedStyles.copyButton}
                                        onClick={() => this.fileInput?.click()}>📁 Upload File</button>
                                </div>
                                <textarea className={`${sharedStyles.textarea} ${error ? sharedStyles.inputError : ''}`}
                                    placeholder={this.isCsvToJson ? 'name,age,city\nAlice,30,NYC' : '[{"name":"Alice","age":30}]'}
                                    value={input} onChange={e => this.convert(e.target.value)} rows={10} />
                            </div>
                            <div>
                                <div className={styles.header}>
                                    <label className={sharedStyles.label}>{outputLabel} Output</label>
                                    {output && (
                                        <button className={`${sharedStyles.copyButton} ${copied ? sharedStyles.copyButtonSuccess : ''}`}
                                            onClick={this.copy}>{copied ? '✓ Copied' : 'Copy'}</button>
                                    )}
                                </div>
                                <div className={styles.readOnlyWrapper}>
                                    <span className={styles.readOnlyBadge}>Read Only</span>
                                    <textarea className={sharedStyles.textarea} value={output} readOnly rows={10} style={{ background: '#f1f5f9' }} />
                                </div>
                            </div>
                        </div>
                        {error && <div className={sharedStyles.errorMessage}>{error}</div>}
                        {preview.length > 0 && (
                            <div className={styles.preview}>
                                <label className={sharedStyles.label} style={{ marginTop: '1rem' }}>Preview (first {Math.min(preview.length, 5)} rows)</label>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>{preview[0].map((h, i) => <th key={i}>{this.isCsvToJson && hasHeaders ? h : `Col ${i + 1}`}</th>)}</tr>
                                    </thead>
                                    <tbody>
                                        {preview.slice(this.isCsvToJson && hasHeaders ? 1 : 0, 6).map((row, ri) => (
                                            <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{cell}</td>)}</tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}
